import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useRef } from 'react';

import { Button } from '@/components/ui/button';
import { Loader } from '@/components/Loader';
import { MarkdownEditor } from '@/components/markdown/MarkdownEditor';
import { NoData } from '@/components/legacy-ui/NoData';
import { useDownloadFile, useOpenFile } from '@/hooks/useFiles';
import { ParsedFileTypes } from '@/types';
import { detectBinaryFile, getFileType } from '@/utils/file.util';

/**
 * Displays file content (text, image, PDF, binary).
 *
 * IMPORTANT: Callers should render this component with `key={fileName}` so that
 * React resets all internal state when the file changes, instead of relying on
 * effect-driven setState resets.
 */
const FileViewer = ({
  fileName,
  onChange,
  onClose,
  email,
  praetorian,
}: {
  fileName: string;
  onChange?: (value: string) => void;
  onClose: () => void;
  email?: string;
  praetorian?: boolean;
}) => {
  const { mutate: downloadFile } = useDownloadFile(email);
  const {
    mutate: getFileContent,
    data: fileContent,
    isPending: isFetching,
  } = useOpenFile(email);
  const [editorContent, setEditorContent] = React.useState('');
  const [imageUrl, setImageUrl] = React.useState('');
  const [isBinary, setIsBinary] = React.useState(false);
  const [isFileTooLarge, setIsFileTooLarge] = React.useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fileType = fileName ? getFileType(fileName) : '';

  // Fetch file on mount (component is keyed by fileName, so this runs once per file)
  useEffect(() => {
    if (!fileName) return;

    timeoutRef.current = setTimeout(() => {
      setIsFileTooLarge(true);
    }, 3000);

    getFileContent({ name: fileName, praetorian });

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [fileName, getFileContent, praetorian]);

  // Process file content once loaded
  useEffect(() => {
    const processFileContent = async () => {
      if (!fileContent) return;

      // File loaded successfully, clear timeout
      setIsFileTooLarge(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (fileType === ParsedFileTypes.IMAGE) {
        setImageUrl(URL.createObjectURL(fileContent));
      } else if (fileType === ParsedFileTypes.PDF) {
        const blob = new Blob([fileContent], { type: 'application/pdf' });
        const blobURL = URL.createObjectURL(blob);
        window.open(blobURL);
        onClose();
      } else if (fileType === ParsedFileTypes.BINARY) {
        setIsBinary(true);
      } else {
        const isBinaryContent = await detectBinaryFile(fileContent, fileName);

        if (isBinaryContent) {
          setIsBinary(true);
        } else {
          const reader = new FileReader();
          reader.onload = function (event) {
            if (event.target) {
              const content = event.target.result as string;
              setEditorContent(content);
              if (onChange) onChange(content);
            }
          };
          reader.readAsText(fileContent);
        }
      }
    };

    processFileContent();
  }, [fileContent, fileType, fileName, onChange, onClose]);

  const isLoading = isFetching && !fileContent;

  if (isLoading) {
    return <Loader isLoading />;
  }

  if (isFileTooLarge) {
    return (
      <NoData
        title="This file is too large to be displayed"
        action={
          <div className="bg-chariot-background-secondary border-chariot-stroke inline-flex rounded-lg border p-4">
            <Button
              onClick={() => {
                downloadFile({ name: fileName, praetorian });
              }}
            >
              <ArrowDownTrayIcon className="size-5" />
              Download File
            </Button>
          </div>
        }
      />
    );
  }

  if (isBinary) {
    return (
      <NoData
        title="This file type cannot be displayed"
        action={
          <div className="bg-chariot-background-secondary border-chariot-stroke inline-flex rounded-lg border p-4">
            <Button
              onClick={() => {
                downloadFile({ name: fileName, praetorian });
              }}
            >
              <ArrowDownTrayIcon className="size-5" />
              Download File
            </Button>
          </div>
        }
      />
    );
  }

  if (fileType === ParsedFileTypes.IMAGE) {
    return <img src={imageUrl} alt="File content" />;
  } else if (fileType === ParsedFileTypes.DOCUMENT) {
    return (
      <div className="h-[60vh]">
        <MarkdownEditor
          value={editorContent}
          onChange={value => {
            setEditorContent(value ?? '');
            if (onChange) onChange(value ?? '');
          }}
          filePathPrefix="proof-of-exploit/files"
        />
      </div>
    );
  }

  return null;
};

export default FileViewer;
